import { Address } from '../../core';

export class AddressUtils {
  private static readonly OPTIONAL_BLOCK_REGEX = /\[([^[\]]+)]/g;
  private static readonly ADDRESS_TOKENS = [
    'Attention',
    'Address1',
    'Address2',
    'City',
    'StateCode',
    'StateName',
    'PostalCode',
    'CountryCode',
    'CountryName',
  ] as const;

  private static tokenRegex(token: string): RegExp {
    return new RegExp(`\\b${token}\\b`, 'g');
  }

  static format(address: Address | null | undefined, format: string): string {
    if (!address) return '';

    const resolveToken = (token: string): string => {
      switch (token) {
        case 'Attention':
          return address.attention ?? '';
        case 'Address1':
          return address.addressLine1 ?? '';
        case 'Address2':
          return address.addressLine2 ?? '';
        case 'City':
          return address.city ?? '';
        case 'StateCode':
          return address.state?.stateCode ?? '';
        case 'StateName':
          return address.state?.name ?? '';
        case 'PostalCode':
          return address.postalCode ?? '';
        case 'CountryCode':
          return address.country?.countryCode ?? '';
        case 'CountryName':
          return address.country?.name ?? '';
        default:
          return '';
      }
    };

    const renderTokens = (segment: string): string =>
      this.ADDRESS_TOKENS.reduce(
        (result, token) => result.replace(this.tokenRegex(token), resolveToken(token)),
        segment,
      );

    const renderedOptionals = format.replace(this.OPTIONAL_BLOCK_REGEX, (_, block: string) => {
      const hasValue = this.ADDRESS_TOKENS.some(token => this.tokenRegex(token).test(block) && !!resolveToken(token));
      return hasValue ? renderTokens(block) : '';
    });

    return renderTokens(renderedOptionals).trim();
  }

  static formatAddressToString(address: Address | undefined): string {
    return this.format(address, '[Address1, ][Address2, ]City[, StateCode][ PostalCode]');
  }

  static formatAddressToSlightlySmallerString(address: Address | undefined): string {
    return this.format(address, '[Address1, ][Address2, ]City[, StateCode]');
  }

  static formatFacilityAddressToLocationString(address: Address | undefined, facilityName: string | undefined): string {
    return this.format(address, `${facilityName}[ – City, StateName]`);
  }
}
